import { Fragment, type ReactNode } from 'react';

/**
 * Minimal JSON syntax highlighter for request/response body previews —
 * regex-token-based rather than a real parser, since stored bodies are
 * truncated strings (possibly ending in "…(truncated)") that wouldn't
 * survive JSON.parse. Ported from a sibling project's identical component
 * (contractor-saas's JsonCodeBlock), swapped to this app's Tailwind colors.
 */
const TOKEN =
  /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

function highlight(json: string): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = TOKEN.exec(json)) !== null) {
    if (match.index > lastIndex) {
      out.push(<Fragment key={key++}>{json.slice(lastIndex, match.index)}</Fragment>);
    }
    const [text, propKey, str, keyword, num] = match;
    let cls = 'text-slate-800';
    if (propKey) cls = 'text-blue-600';
    else if (str) cls = 'text-green-700';
    else if (keyword) cls = 'text-red-600';
    else if (num) cls = 'text-amber-600';
    out.push(
      <span key={key++} className={cls}>
        {text}
      </span>,
    );
    lastIndex = match.index + text.length;
  }
  if (lastIndex < json.length) {
    out.push(<Fragment key={key++}>{json.slice(lastIndex)}</Fragment>);
  }
  return out;
}

export function JsonCodeBlock({ json }: { json: string }) {
  return (
    <pre className="overflow-x-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed">
      <code className="font-mono">{highlight(json)}</code>
    </pre>
  );
}
